import { memo } from 'react'
import DataTable, { IDataTableColumn } from 'react-data-table-component'
import { GS, GS_m } from 'back-end-api'
import { useTranslation } from 'services/Translation'
import LoaderMini from 'components/common/loaders/mini'

import texts from '../texts'
import {
  TimeFilterValue,
  buildTimeFilterParams,
  isCustomRangeValid,
  productLabel,
  formatCents,
} from '../helpers'
import { Panel, TotalCard, TotalChip, Notice, EmptyState } from '../Styles'
import StripeWalletLink from '../StripeWalletLink'

interface FinancialMetricsProps {
  filter: TimeFilterValue
  // Whether the doctor has completed onboarding; gates the Stripe wallet link.
  onboarded: boolean
}

const columns = (
  t: (key: string) => string
): IDataTableColumn<GS_m.AnalyticsFinancialProductRow>[] => [
  {
    name: t('product'),
    cell: r => productLabel(r.product, t),
    minWidth: '140px',
  },
  {
    name: t('transactions'),
    selector: 'transaction_count',
    center: true,
    cell: r => r.transaction_count ?? 0,
  },
  {
    name: t('gross'),
    right: true,
    cell: r => formatCents(r.gross_cents, r.currency),
  },
  {
    name: t('commission'),
    right: true,
    cell: r => formatCents(r.commission_cents, r.currency),
  },
  {
    name: t('net'),
    right: true,
    cell: r => formatCents(r.net_cents, r.currency),
  },
  {
    name: t('refundTotal'),
    right: true,
    cell: r => formatCents(r.refund_total_cents, r.currency),
  },
  {
    name: t('refundCount'),
    center: true,
    cell: r => r.refund_count ?? 0,
  },
]

// Task 15.3 — Financial Metrics tab (Requirements 6.1, 6.2, 6.3, 6.4).
// Displays the per-product transactional history rows plus a per-currency
// total-earnings summary. Amounts are shown in their originating currency with
// no conversion (6.4). The Stripe wallet link sits below the table (11.1).
const FinancialMetrics = ({ filter, onboarded }: FinancialMetricsProps) => {
  const { t } = useTranslation(texts)

  const rangeValid = isCustomRangeValid(filter)
  const params: GS_m.GetWalletFinancialMetricsParams = buildTimeFilterParams(filter)

  const { data, error, isLoading } = GS.useWalletFinancialMetrics(params, {
    // Skip fetching while a custom range is incomplete/invalid.
    enabled: rangeValid,
  })

  const totals = data?.total_earnings ?? []
  const history = data?.history ?? []

  // While a custom range is incomplete/inverted, show a neutral prompt instead
  // of a backend validation error (the TimeFilter renders the detailed warning).
  if (!rangeValid) {
    return (
      <Panel>
        <EmptyState>{t('invalidRange')}</EmptyState>
      </Panel>
    )
  }

  return (
    <Panel>
      {/* Total earnings per originating currency (Requirement 6.1). */}
      <TotalCard data-cy="totalEarnings">
        {totals.length === 0 ? (
          <TotalChip>
            <span>{t('totalEarnings')}</span>
            <strong>{formatCents(0, 'EUR')}</strong>
          </TotalChip>
        ) : (
          totals.map(amount => (
            <TotalChip key={amount.currency ?? 'EUR'}>
              <span>{t('totalEarnings')}</span>
              <strong>{formatCents(amount.amount_cents, amount.currency)}</strong>
            </TotalChip>
          ))
        )}
      </TotalCard>

      {error ? (
        <Notice variant="error" data-cy="financialError">
          {t('errorLoading')}
        </Notice>
      ) : (
        <DataTable
          noHeader
          columns={columns(t)}
          data={history}
          progressPending={isLoading}
          progressComponent={<LoaderMini />}
          noDataComponent={<EmptyState>{t('empty')}</EmptyState>}
          dense
          highlightOnHover
        />
      )}

      {/* Stripe wallet link below the metrics table — only when onboarded. */}
      {onboarded && <StripeWalletLink />}
    </Panel>
  )
}

FinancialMetrics.whyDidYouRender = true

export default memo(FinancialMetrics)
